import {
  Filter,
  FilterOperator,
  FilterOperatorAndValue,
  FilterOption,
  FilterType,
  SavedFilter,
} from 'app/modules/filters/models';

import {
  FILTER_OPERATOR,
  FILTER_TYPE_OPERATORS,
} from 'app/modules/filters/constants';
import { isEqual, keyBy } from 'lodash';

// Compares two filter sets ignoring order — applying status then type is
// equivalent to type then status. Filters are unique by `key`, so we compare
// keyed maps rather than the raw arrays (lodash isEqual is order-sensitive).
export const areFiltersEqual = (a: Filter[], b: Filter[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  return isEqual(keyBy(a, 'key'), keyBy(b, 'key'));
};

export const isOperatorAndValueValid = (
  operatorAndValue: any,
): operatorAndValue is FilterOperatorAndValue => {
  try {
    const { operator, value } = operatorAndValue;
    switch (operator) {
      case FILTER_OPERATOR.CONTAINS_TEXT:
      case FILTER_OPERATOR.IS_EXACT_TEXT:
      case FILTER_OPERATOR.IS_NOT_EXACT_TEXT:
        return typeof value === 'string' && value !== '';

      case FILTER_OPERATOR.IS:
        return (
          (typeof value === 'string' && value !== '') ||
          typeof value === 'number'
        );

      case FILTER_OPERATOR.IS_AFTER_DATE:
      case FILTER_OPERATOR.IS_BEFORE_DATE:
        return !isNaN(new Date(value).getTime());

      case FILTER_OPERATOR.IS_BETWEEN_DATE: {
        if (!Array.isArray(value)) {
          return false;
        }
        const [start, end] = value;
        const startDate = new Date(start).getTime();
        const endDate = new Date(end).getTime();
        return !isNaN(startDate) && !isNaN(endDate) && endDate > startDate;
      }

      case FILTER_OPERATOR.IS_BETWEEN_NUMBER:
      case FILTER_OPERATOR.IS_BETWEEN_SLIDER: {
        if (!Array.isArray(value)) {
          return false;
        }
        const [min, max] = value;
        return typeof min === 'number' && typeof max === 'number' && max > min;
      }

      case FILTER_OPERATOR.IS_GREATER_THAN_NUMBER:
      case FILTER_OPERATOR.IS_LESS_THAN_NUMBER:
        return typeof value === 'number';

      case FILTER_OPERATOR.IS_ONE_OF:
      case FILTER_OPERATOR.IS_NOT_ONE_OF: {
        if (!Array.isArray(value) || !value.length) {
          return false;
        }
        return value.every(
          (i) => typeof i === 'string' || typeof i === 'number',
        );
      }

      case FILTER_OPERATOR.IS_EMPTY:
      case FILTER_OPERATOR.IS_NOT_EMPTY:
      case FILTER_OPERATOR.IS_TRUE:
      case FILTER_OPERATOR.IS_FALSE: {
        return true;
      }

      default:
        return false;
    }
  } catch {
    return false;
  }
};

export const getValidFilters = (
  something: any,
  filterOptions: FilterOption[],
): Filter[] => {
  if (!Array.isArray(something)) {
    return [];
  }

  const filterOptionsMap = filterOptions.reduce(
    (acc, i) => ({ ...acc, [i.key]: i }),
    {},
  );
  try {
    return something.reduce<Filter[]>((acc, i) => {
      if (!i || typeof i !== 'object') {
        return acc;
      }
      const { key, ...value } = i;
      // check whether value matches operator
      if (!isOperatorAndValueValid(value)) {
        return acc;
      }
      const { operator } = value;

      const option = filterOptionsMap[key];
      if (!option) {
        return acc;
      }
      if (
        FILTER_TYPE_OPERATORS[option.type]?.find((j) => j.value === operator)
      ) {
        acc.push(i);
      }
      return acc;
    }, []);
  } catch {
    return [];
  }
};

// Resolves a saved filter's stored filters into the concrete set to apply:
// drops filters whose option is gone/invalid and re-adds any required default
// filters the saved set omits.
export const resolveSavedFilters = (
  saved: Filter[],
  options: FilterOption[],
  defaultFilters: Filter[],
): Filter[] => {
  const valid = getValidFilters(saved, options);
  const validKeys = new Set(valid.map((i) => i.key));
  const optionMap = options.reduce<Record<FilterOption['key'], FilterOption>>(
    (acc, i) => ({ ...acc, [i.key]: i }),
    {},
  );
  const missingRequired = defaultFilters.filter(
    (i) => optionMap[i.key]?.required && !validKeys.has(i.key),
  );
  return [...missingRequired, ...valid];
};

export const getFilterOperators = (
  type: FilterType,
  customize?: Partial<Record<FilterOperator, { disabled?: true }>>,
): ValueOf<typeof FILTER_TYPE_OPERATORS> => {
  const operatorOptions = FILTER_TYPE_OPERATORS[type];
  return operatorOptions.filter((i) => !customize?.[i.value]?.disabled);
};

export const orderSavedFilters = (
  savedFilters: SavedFilter[],
  storedOrder: number[],
): SavedFilter[] => {
  const byId = new Map(savedFilters.map((i) => [i.id, i]));
  const usedIds = new Set<number>();
  const ordered: SavedFilter[] = [];
  for (const id of storedOrder) {
    const sf = byId.get(id);
    if (sf && !usedIds.has(id)) {
      ordered.push(sf);
      usedIds.add(id);
    }
  }
  const remaining = savedFilters
    .filter((i) => !usedIds.has(i.id))
    .toSorted((a, b) =>
      a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
    );
  return [...ordered, ...remaining];
};
